// Reine, I/O-freie Begrüßungs-Logik für die Übersicht (Kopf): Tageszeit-Gruß, Vorname, tickender
// Uhr-/Datums-Text und der „Spruch des Tages" (deterministisch je Kalendertag). Bewusst OHNE Intl/
// toLocale* — die Formate sind hier selbst gebaut, damit Tests umgebungs- und zeitzonenunabhängig grün
// bleiben (vgl. die dokumentierte UTC-/Locale-Falle des Altcodes). Lokale Zeit über Date-Getter.

/** Tageszeit-Gruß nach Stunde (lokal): Morgen 5–11, Tag 11–17, Abend 17–22, sonst Nacht. */
export function greetingForHour(hour: number): string {
  if (hour >= 5 && hour < 11) return 'Guten Morgen'
  if (hour >= 11 && hour < 17) return 'Guten Tag'
  if (hour >= 17 && hour < 22) return 'Guten Abend'
  return 'Gute Nacht'
}

/** Erstes Wort des Namens als Anrede. Leerer/blanker Name → '' (der Aufrufer grüßt dann ohne Namen). */
export function firstName(fullName: string): string {
  const parts = fullName.trim().split(/\s+/).filter(Boolean)
  return parts[0] ?? ''
}

/** „Guten Morgen, Simon!" bzw. „Guten Morgen!" ohne Namen. */
export function greetingLine(now: Date, fullName: string): string {
  const greeting = greetingForHour(now.getHours())
  const name = firstName(fullName)
  return name ? `${greeting}, ${name}!` : `${greeting}!`
}

const WEEKDAYS = ['Sonntag', 'Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag', 'Samstag'] as const
const MONTHS = [
  'Januar', 'Februar', 'März', 'April', 'Mai', 'Juni',
  'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember',
] as const

/** „Donnerstag, 18. Juni 2026" (lokal, ohne Intl → testbar). */
export function formatLongDate(now: Date): string {
  return `${WEEKDAYS[now.getDay()]}, ${now.getDate()}. ${MONTHS[now.getMonth()]} ${now.getFullYear()}`
}

/** „09:07:03" (lokal, immer zweistellig). */
export function formatClock(now: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${pad(now.getHours())}:${pad(now.getMinutes())}:${pad(now.getSeconds())}`
}

// Werkstatt-/Motivationssprüche in Marken-Stimme (du, ehrlich, kein Fachchinesisch). Erweiterbar —
// die Auswahl ist rein index-basiert, also bleibt jede Ergänzung deterministisch.
export const SPRUECHE: readonly string[] = [
  'Jeder große Auftrag war mal ein leeres Formular.',
  'Erst die Diagnose, dann der Schrauber – gilt fürs Auto und für den Tag.',
  'Ein aufgeräumter Hof ist ein halber Feierabend.',
  'Kunden merken sich, wie pünktlich du warst – nicht wie sehr du gehetzt hast.',
  'Der beste Kostenvoranschlag ist der, den der Kunde versteht.',
  'Sauber dokumentiert ist halb bezahlt.',
  'Auch der längste Reifenwechsel beginnt mit dem ersten Radbolzen.',
  'Wer den Werkstatttag plant, lässt ihn nicht den Tag planen.',
  'Gute Arbeit spricht sich rum – schlechte schneller.',
  'Ordnung im Auftrag, Ruhe im Kopf.',
  'Ein ehrlicher Befund ist mehr wert als ein schneller.',
  'Heute schon ein Auto glücklich gemacht?',
  'Kaffee an, Hebebühne hoch – los geht’s.',
  'Feierabend ist, wenn der letzte Auftrag sauber steht.',
]

/**
 * Spruch des Tages: deterministisch aus dem lokalen Kalenderdatum (genau 1 pro Tag, rotiert über die
 * Liste). Bewusst datums- statt zufallsbasiert, damit Reload/Re-Render denselben Spruch zeigen.
 */
export function spruchOfDay(now: Date): string {
  const dayKey = now.getFullYear() * 10000 + (now.getMonth() + 1) * 100 + now.getDate()
  return SPRUECHE[dayKey % SPRUECHE.length] ?? SPRUECHE[0]!
}
